import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Auth0Provider } from './components/Auth0Provider'
import { AuthProvider } from './hooks/useAuth'
import { UnitPreferenceProvider } from './contexts/UnitPreferenceContext'
import { useAuth0 } from '@auth0/auth0-react'
import { Layout } from './components/Layout'
import { AlarmsPage } from './components/pages/AlarmsPage'
import { DevicesPage } from './components/pages/DevicesPage'
import { ProfilePage } from './components/pages/ProfilePage'
import { LoginPage } from './components/pages/LoginPage'
import { DashboardPage } from './components/pages/DashboardPage'
import { ZonesPage } from './components/pages/ZonesPage'
import { pushNotificationService } from './services/pushNotifications'
import { useEffect } from 'react'
import './App.css'
import './barcode-scanner.css'

// NOTE: Token exchange is now handled automatically by GenericOAuth2 plugin
// No need for manual deep link handling

// Commented out - no longer needed with GenericOAuth2 plugin
/*
const exchangeCodeForTokens = async (code: string, _state: string) => {
  console.log('📱 Exchanging authorization code for tokens')

  const tokenUrl = 'https://dev-l5r36ha70e832gto.us.auth0.com/oauth/token'
  const tokenData = {
    grant_type: 'authorization_code',
    client_id: 'zadRooVy8WP1VjvI46xvt4S6KNtLdTVb',
    code: code,
    redirect_uri: 'com.silverfallscapital.omniiqmobile://callback',
  }

  try {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tokenData),
    })

    const tokens = await response.json()

    if (!response.ok) {
      console.error('📱 Token exchange failed:', tokens)
      throw new Error(tokens.error_description || 'Token exchange failed')
    }

    console.log('📱 Token exchange successful:', tokens)

    try {
      console.log('📱 CHECKPOINT: About to process tokens and store in localStorage')

      // Store tokens in localStorage for Auth0React to pick up
      const scope = 'openid profile email read:devices read:device_data read:locations read:events read:alarms'
      const storageKey = `@@auth0spajs@@::zadRooVy8WP1VjvI46xvt4S6KNtLdTVb::https://test-api.strata47.com::${scope}`
      console.log('📱 CHECKPOINT: Generated storage key:', storageKey)

      // Parse JWT token for Auth0React compatibility
      console.log('📱 CHECKPOINT: About to decode JWT token')
      let userFromToken = null
      try {
        const idTokenPayload = tokens.id_token.split('.')[1]
        const decodedPayload = JSON.parse(atob(idTokenPayload))
        userFromToken = decodedPayload
        console.log('📱 CHECKPOINT: JWT decoded successfully:', userFromToken)
      } catch (error) {
        console.error('📱 Error decoding ID token:', error)
      }

      // Format tokens exactly as Auth0React expects (with refresh token)
      const auth0TokenPayload = {
        body: {
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token || null,
          id_token: tokens.id_token,
          scope: tokens.scope,
          expires_in: tokens.expires_in,
          token_type: tokens.token_type,
          decodedToken: {
            claims: userFromToken,
            user: userFromToken
          }
        },
        expiresAt: Math.floor(Date.now() / 1000) + tokens.expires_in,
      }

      console.log('📱 CHECKPOINT: About to store tokens in localStorage')
      console.log('📱 Auth0TokenPayload structure:', JSON.stringify(auth0TokenPayload, null, 2))

      try {
        localStorage.setItem(storageKey, JSON.stringify(auth0TokenPayload))
        console.log('📱 CHECKPOINT: localStorage.setItem completed successfully')
      } catch (error) {
        console.error('📱 ERROR: Failed to store in localStorage:', error)
      }
      console.log('📱 Stored Auth0 tokens with key:', storageKey)
      console.log('📱 Stored data:', auth0TokenPayload)

      // Immediately verify what was stored
      const storedValue = localStorage.getItem(storageKey)
      console.log('📱 Verification - what was actually stored:', storedValue)

      // Show all localStorage keys for debugging
      console.log('📱 All localStorage keys after storage:')
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        console.log(`📱 Key ${i}: ${key}`)
        if (key && key.includes('auth0')) {
          const value = localStorage.getItem(key)
          console.log(`📱 Auth0 key content:`, value)
        }
      }

      // Clean up stored state
      localStorage.removeItem('auth0_state')
      localStorage.removeItem('auth0_webview_state')

      // Force reload to pick up new auth state
      console.log('📱 Authentication complete, reloading app')
      // Use a small delay to ensure state is saved
      setTimeout(() => {
        window.location.reload()
      }, 100)

    } catch (processingError) {
      console.error('📱 ERROR: Token processing failed:', processingError)
      console.error('📱 ERROR: Token processing error details:', (processingError as Error).message)
      console.error('📱 ERROR: Token processing stack:', (processingError as Error).stack)
    }

  } catch (error) {
    console.error('📱 Error exchanging code for tokens:', error)
    throw error
  }
}
*/

// Auth0 callback handler component - Auth0 SDK handles this automatically
const Auth0Callback = () => {
  const { isAuthenticated, isLoading } = useAuth0()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // User is authenticated, redirect to main app
      navigate('/dashboard')
    } else if (!isLoading && !isAuthenticated) {
      // Authentication failed, redirect to login
      navigate('/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
      <div>Processing login...</div>
    </div>
  )
}

function App() {
  useEffect(() => {
    // Initialize push notifications when app starts
    pushNotificationService.initialize().catch(error => {
      console.error('Failed to initialize push notifications:', error)
    })

    // NOTE: Deep link handling is now done by the GenericOAuth2 plugin automatically
    // The plugin handles the OAuth callback and token exchange internally
  }, [])

  return (
    <Router>
      <Auth0Provider>
        <AuthProvider>
          <UnitPreferenceProvider>
            <div className="app">
              <Routes>
                <Route path="/callback" element={<Auth0Callback />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/zones" element={<ZonesPage />} />
                  <Route path="/devices" element={<DevicesPage />} />
                  <Route path="/alarms" element={<AlarmsPage />} />
                  <Route path="/settings" element={<ProfilePage />} />
                  <Route path="/profile" element={<Navigate to="/settings" replace />} />
                </Route>
              </Routes>
            </div>
          </UnitPreferenceProvider>
        </AuthProvider>
      </Auth0Provider>
    </Router>
  )
}

export default App
